"use client";

import { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ArrowUpRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import type { TrainingBrief } from "@/lib/types/training";
import { Button } from "@workspace/ui/components";
import { TrainingHeader } from "../../components/training-header";
import { SectionCard } from "./section-card";
import { InfoAlert } from "./info-alert";
import { TrainingInfoDialog } from "./training-info-dialog";
import { useLandingStore } from "@/lib/store/landing-store.context";
import { trackSessionEvent } from "@/lib/analytics/events";
import Link from "next/link";

type ProspectBriefProps = {
  brief: TrainingBrief;
};

function ProspectBriefComponent({ brief }: ProspectBriefProps) {
  const router = useRouter();
  const store = useLandingStore();
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "1",
    "2",
    "3",
    "4",
  ]);

  const handleStartRoleplay = async () => {
    try {
      setIsCreatingSession(true);

      const sessionData = await store.session.createSession(
        "discovery_qualification",
      );

      router.push(`/training/session/${sessionData.id}`);
    } catch (error) {
      console.error("[ProspectBrief] Failed to create session:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      trackSessionEvent("session_error", {
        sessionId: "unknown",
        errorMessage,
      });

      toast.error("Failed to start training. Please try again.");
      setIsCreatingSession(false);
    }
  };

  const sections = [
    {
      number: "1",
      title: "Can you save your SaaS?",
      content: brief.meetingContext,
    },
    {
      number: "2",
      title: "Your Angle",
      content: brief.yourAngle,
    },
    {
      number: "3",
      title: "What You'll Discover",
      content: brief.whatYouDiscover,
    },
    {
      number: "4",
      title: "Why this matters?",
      content: brief.whyThisMatters,
    },
  ];

  const toggleSection = (number: string) => {
    setExpandedSections((prev) =>
      prev.includes(number)
        ? prev.filter((n) => n !== number)
        : [...prev, number],
    );
  };

  return (
    <div className="w-full min-h-screen pb-48 md:pb-0 bg-d-secondary">
      {/* Training Info Dialog */}
      <TrainingInfoDialog />

      {/* Header */}
      <TrainingHeader title="Marcus Challenge" />

      {/* Main Content */}
      <div className="flex flex-col gap-12 items-center pb-16 px-5 md:px-10 w-full min-h-screen">
        {/* Desktop Content */}
        <div className="hidden md:flex flex-col gap-6 items-start w-full max-w-3xl">
          {/* Main white card containing Marcus + Meeting Context */}
          <div className="bg-white border border-d-border flex flex-col gap-6 p-5 rounded-[32px] shadow-sm w-full">
            {/* Marcus Card - Desktop inline version */}
            <div className="bg-[#fff3dc] border border-d-border flex flex-col items-start px-8 py-10 rounded-xl w-full">
              <div className="flex gap-6 items-center w-full">
                <div className="relative bg-white border-[3px] border-d-black rounded-full shadow-[4px_4px_0px_0px_#292524] shrink-0 size-24 overflow-hidden">
                  <Image
                    src="/marcus.png"
                    alt={brief.prospect.name}
                    width={200}
                    height={200}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2 items-start">
                  <p className="font-extrabold leading-8 text-d-black text-3xl w-full">
                    {brief.prospect.name}
                  </p>
                  <p className="font-medium leading-6 text-d-gray text-lg w-full">
                    {brief.prospect.role}
                  </p>
                </div>
                <Button
                  variant="d-primary"
                  className="h-12"
                  onClick={handleStartRoleplay}
                  disabled={isCreatingSession}
                >
                  {isCreatingSession ? "Starting..." : "Start Roleplay"}
                </Button>
              </div>
            </div>

            {/* Meeting Context Section */}
            <div className="flex flex-col gap-6 items-start w-full">
              <div className="flex flex-col gap-5 items-start w-full">
                <p className="font-semibold leading-7 text-d-black text-xl">
                  Meeting Context
                </p>
                <div className="flex flex-col gap-4 items-start w-full">
                  {sections.map((section, idx) => (
                    <SectionCard
                      key={idx}
                      number={section.number}
                      title={section.title}
                      content={section.content}
                    />
                  ))}
                </div>
              </div>
              <InfoAlert>
                Before starting this activity, please find a quiet place with
                minimal distractions. You&apos;ll need a microphone and a video
                camera to participate in this activity.
              </InfoAlert>
            </div>
          </div>

          {/* Desktop Sign Up Button */}
          <div className="flex justify-center w-full">
            <Button variant="d-default" className="gap-2.5 h-12" asChild>
              <Link href="/auth">
                Sign Up for free
                <ArrowUpRight className="w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="md:hidden w-full flex flex-col gap-6">
          <h1 className="font-semibold text-2xl leading-8 text-d-black">
            Meeting Context
          </h1>
          <div className="bg-white border border-d-border flex flex-col gap-5 p-4 rounded-3xl shadow-sm w-full">
            {sections.map((section, idx) => (
              <SectionCard
                key={idx}
                number={section.number}
                title={section.title}
                content={section.content}
                isExpanded={expandedSections.includes(section.number)}
                onToggle={() => toggleSection(section.number)}
              />
            ))}
            <InfoAlert>
              Before starting this activity, please find a quiet place with
              minimal distractions. You&apos;ll need a microphone and a video
              camera to participate in this activity.
            </InfoAlert>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Prospect Section */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-20">
        <div className="bg-white border-t border-l border-r border-d-border flex flex-col gap-6 items-center justify-center p-5 rounded-tl-3xl rounded-tr-3xl">
          <div className="flex gap-4 h-14 items-center w-full">
            <div className="bg-white border-[1.75px] border-d-black rounded-full shadow-[2.333px_2.333px_0px_0px_#292524] shrink-0 size-14 flex items-center justify-center overflow-hidden">
              <Image
                src="/marcus.png"
                alt={brief.prospect.name}
                width={200}
                height={200}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-1 flex-col gap-1 items-start">
              <p className="font-extrabold leading-5 text-d-black text-base">
                {brief.prospect.name}
              </p>
              <p className="font-medium leading-4 text-d-gray text-[13px]">
                {brief.prospect.role}
              </p>
            </div>
          </div>
          <Button
            variant="d-primary"
            className="h-10 text-base px-4 w-full"
            onClick={handleStartRoleplay}
            disabled={isCreatingSession}
          >
            {isCreatingSession ? "Starting..." : "Start Roleplay"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export const ProspectBrief = observer(ProspectBriefComponent);
